/**
 * PaymentSuccessPage
 * ──────────────────
 * Handles three distinct flows that all land on /payment/success:
 *
 *  1. Stripe Checkout session  (dynamic checkout — buy-now via API)
 *     URL: /payment/success?session_id=cs_xxx
 *     → polls /api/payments/status/:session_id until paid | timeout | error
 *
 *  2. Stripe Payment Link  (pre-built link — buy-now via stripe_payment_link field)
 *     URL: /payment/success?payment_intent=pi_xxx&redirect_status=succeeded
 *     → shows instant success — no backend poll needed (Stripe already confirmed)
 *
 *  3. No params at all
 *     → graceful "check your email" state
 *
 * Post-payment: if user is logged in, shows a 4-second interstitial then
 * redirects to /account. Guests stay on the success page with CTA buttons.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CheckCircle2, ArrowRight, Loader2, AlertCircle, Clock, Package, Mail, User } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_DEV;
const API = `${BACKEND_URL}/api`;

// ── Shared design atoms ────────────────────────────────────────────────────
const Card = ({ children, wide = false }) => (
  <div
    className="min-h-screen flex items-center justify-center px-4 sm:px-6"
    style={{ paddingTop: 80, paddingBottom: 60, background: "hsl(var(--background))" }}
  >
    <div
      style={{
        maxWidth: wide ? 560 : 440,
        width: "100%",
        background: "#fff",
        borderRadius: 20,
        border: "1px solid hsl(var(--border))",
        padding: "44px 40px",
        textAlign: "center",
        boxShadow: "0 12px 60px rgba(106,0,244,0.07), 0 2px 16px rgba(0,0,0,0.04)",
      }}
    >
      {children}
    </div>
  </div>
);

const IconCircle = ({ bg, children }) => (
  <div
    style={{
      width: 76,
      height: 76,
      borderRadius: "50%",
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 28px",
    }}
  >
    {children}
  </div>
);

const Heading = ({ children }) => (
  <h1
    style={{
      fontFamily: "'Oswald', sans-serif",
      fontWeight: 600,
      fontSize: "1.85rem",
      color: "hsl(var(--foreground))",
      letterSpacing: "-0.01em",
      lineHeight: 1.15,
      margin: "0 0 14px",
    }}
  >
    {children}
  </h1>
);

const Body = ({ children }) => (
  <p
    style={{
      fontFamily: "'Manrope', sans-serif",
      fontSize: 14,
      color: "hsl(var(--muted-foreground))",
      lineHeight: 1.65,
      margin: "0 0 24px",
    }}
  >
    {children}
  </p>
);

const PrimaryBtn = ({ onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      width: "100%",
      height: 50,
      borderRadius: 12,
      marginTop: 10,
      background: "linear-gradient(135deg, #6a00f4, #d100d1)",
      color: "#fff",
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 700,
      fontSize: 14,
      letterSpacing: "0.02em",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      boxShadow: "0 4px 20px rgba(106,0,244,0.25)",
      transition: "transform 0.15s, box-shadow 0.15s",
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 28px rgba(106,0,244,0.35)"; }}
    onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(106,0,244,0.25)"; }}
  >
    {children}
  </button>
);

const GhostBtn = ({ onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      width: "100%",
      height: 46,
      borderRadius: 12,
      marginTop: 10,
      background: "transparent",
      color: "hsl(var(--muted-foreground))",
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 600,
      fontSize: 13,
      border: "1px solid hsl(var(--border))",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      transition: "border-color 0.15s, color 0.15s",
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = "#6a00f4"; e.currentTarget.style.color = "#6a00f4"; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = "hsl(var(--border))"; e.currentTarget.style.color = "hsl(var(--muted-foreground))"; }}
  >
    {children}
  </button>
);

// ── What-happens-next timeline (shared by both success states) ────────────
const NextSteps = () => {
  const steps = [
    { icon: <Mail size={16} />, label: "Confirmation email sent", detail: "Check your inbox for order details and receipt." },
    { icon: <Package size={16} />, label: "Order processing begins", detail: "Our team receives your order and begins production review." },
    { icon: "🚚", label: "Shipping & tracking", detail: "You'll receive a tracking number once your machine ships." },
  ];
  return (
    <div
      style={{
        background: "#faf9ff",
        borderRadius: 14,
        padding: "18px 20px",
        marginBottom: 24,
        textAlign: "left",
        border: "1px solid rgba(106,0,244,0.09)",
      }}
    >
      <p
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 9,
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          color: "#9ca3af",
          margin: "0 0 14px",
        }}
      >
        What happens next
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {steps.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "rgba(106,0,244,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: "#6a00f4",
                fontSize: typeof s.icon === "string" ? 15 : undefined,
              }}
            >
              {s.icon}
            </div>
            <div>
              <p style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 13, color: "#111827", margin: "0 0 2px" }}>
                {s.label}
              </p>
              <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: 12, color: "#6b7280", margin: 0, lineHeight: 1.5 }}>
                {s.detail}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Amount badge ──────────────────────────────────────────────────────────
const AmountBadge = ({ amount, currency }) => (
  <div
    style={{
      background: "#f8f7ff",
      borderRadius: 12,
      padding: "14px 20px",
      marginBottom: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      border: "1px solid rgba(106,0,244,0.1)",
    }}
  >
    <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: "#9ca3af", margin: 0 }}>
      Amount Paid
    </p>
    <p style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: "1.4rem", color: "#6a00f4", margin: 0 }}>
      {amount} {currency?.toUpperCase()}
    </p>
  </div>
);

// ── Redirect interstitial for logged-in users ─────────────────────────────
const RedirectInterstitial = ({ countdown, userName }) => (
  <Card>
    <IconCircle bg="linear-gradient(135deg, rgba(106,0,244,0.12), rgba(209,0,209,0.1))">
      <User style={{ height: 32, width: 32, color: "#6a00f4" }} />
    </IconCircle>
    <Heading>Welcome back, {userName}!</Heading>
    <Body>
      Your order has been placed successfully. Redirecting you to your account
      where you can track your order.
    </Body>

    {/* Progress bar */}
    <div style={{ width: "100%", height: 4, borderRadius: 2, background: "#f0efff", overflow: "hidden", marginBottom: 16 }}>
      <div
        style={{
          height: "100%",
          borderRadius: 2,
          background: "var(--grad-primary)",
          transition: "width 1s linear",
          width: `${((4 - countdown) / 4) * 100}%`,
        }}
      />
    </div>

    <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9ca3af", margin: 0 }}>
      Redirecting in {countdown}s...
    </p>

    <style>{`
      @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    `}</style>
  </Card>
);

// ──────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────────────────────
export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((s) => s.auth);

  // URL params from both flows
  const sessionId       = searchParams.get("session_id");
  const paymentIntent   = searchParams.get("payment_intent");
  const redirectStatus  = searchParams.get("redirect_status");

  // Determine initial state based on URL params
  const getInitialStatus = () => {
    if (sessionId) return "checking";
    if (paymentIntent && redirectStatus === "succeeded") return "payment_link_success";
    return "no-session";
  };

  const [status, setStatus]           = useState(getInitialStatus);
  const [paymentData, setPaymentData] = useState(null);
  const [countdown, setCountdown]     = useState(4);
  const [redirecting, setRedirecting] = useState(false);
  const attemptsRef = useRef(0);

  // ── Flow 1: Poll checkout session status ───────────────────────────────
  const pollPaymentStatus = useCallback(async () => {
    const MAX = 8;
    const INTERVAL = 2000;

    if (attemptsRef.current >= MAX) { setStatus("timeout"); return; }

    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentData(res.data);
      if (res.data.payment_status === "paid") {
        setStatus("success");
      } else if (res.data.status === "expired") {
        setStatus("expired");
      } else {
        attemptsRef.current += 1;
        setTimeout(pollPaymentStatus, INTERVAL);
      }
    } catch (err) {
      console.error("Payment status poll error:", err);
      setStatus("error");
    }
  }, [sessionId]);

  // Kick off polling once on mount when a session_id is present.
  useEffect(() => {
    if (sessionId) pollPaymentStatus();
  }, [sessionId, pollPaymentStatus]);

  // ── Auto-redirect for logged-in users on success ───────────────────────
  useEffect(() => {
    const isSuccess = status === "success" || status === "payment_link_success";
    if (!isSuccess || !isAuthenticated || redirecting) return;

    setRedirecting(true);
    setCountdown(4);

    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          navigate("/account", { replace: true });
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status, isAuthenticated, redirecting, navigate]);

  const fmtCents = (cents) =>
    cents != null ? `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : null;

  // ── Interstitial redirect for logged-in users ──────────────────────────
  if (redirecting && isAuthenticated) {
    return <RedirectInterstitial countdown={countdown} userName={user?.first_name || "there"} />;
  }

  // ── Spinner — checking checkout session ───────────────────────────────
  if (status === "checking") return (
    <Card>
      <IconCircle bg="#f8f7ff">
        <Loader2 style={{ height: 36, width: 36, color: "#6a00f4", animation: "spin 1s linear infinite" }} />
      </IconCircle>
      <Heading>Verifying Payment</Heading>
      <Body>Hold on — we're confirming your payment with Stripe. This only takes a moment.</Body>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </Card>
  );

  // ── Flow 1 success: Stripe Checkout session confirmed ─────────────────
  if (status === "success") return (
    <Card wide>
      <IconCircle bg="#dcfce7">
        <CheckCircle2 style={{ height: 36, width: 36, color: "#16a34a" }} />
      </IconCircle>
      <Heading>Payment Confirmed!</Heading>
      <Body>
        Your order is in. Our team has been notified and will start processing right away.
      </Body>

      {paymentData?.amount_total && (
        <AmountBadge
          amount={fmtCents(paymentData.amount_total)}
          currency={paymentData.currency}
        />
      )}

      <NextSteps />

      <PrimaryBtn onClick={() => navigate("/shop")}>
        Browse More Machines <ArrowRight size={16} />
      </PrimaryBtn>
      <GhostBtn onClick={() => navigate("/")}>Back to Home</GhostBtn>
    </Card>
  );

  // ── Flow 2 success: Stripe Payment Link ───────────────────────────────
  if (status === "payment_link_success") return (
    <Card wide>
      {/* Celebration pulse ring */}
      <div style={{ position: "relative", width: 76, height: 76, margin: "0 auto 28px" }}>
        <div
          style={{
            position: "absolute",
            inset: -8,
            borderRadius: "50%",
            background: "rgba(106,0,244,0.08)",
            animation: "pulse 2s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: -16,
            borderRadius: "50%",
            background: "rgba(106,0,244,0.04)",
            animation: "pulse 2s ease-in-out infinite 0.3s",
          }}
        />
        <div
          style={{
            width: 76,
            height: 76,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(106,0,244,0.12), rgba(209,0,209,0.1))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid rgba(106,0,244,0.2)",
          }}
        >
          <CheckCircle2 style={{ height: 38, width: 38, color: "#6a00f4" }} />
        </div>
      </div>

      <Heading>You're all set!</Heading>
      <Body>
        Payment received. Your vending machine order is confirmed and our team
        has been notified. Expect a confirmation email shortly with your receipt
        and order details.
      </Body>

      <NextSteps />

      <PrimaryBtn onClick={() => navigate("/shop")}>
        Browse More Machines <ArrowRight size={16} />
      </PrimaryBtn>
      <GhostBtn onClick={() => navigate("/")}>Back to Home</GhostBtn>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.15); opacity: 0.6; }
        }
      `}</style>
    </Card>
  );

  // ── Timeout ───────────────────────────────────────────────────────────
  if (status === "timeout") return (
    <Card>
      <IconCircle bg="#fefce8">
        <Clock style={{ height: 36, width: 36, color: "#ca8a04" }} />
      </IconCircle>
      <Heading>Still Processing…</Heading>
      <Body>
        Payment received, but confirmation is taking a little longer than usual.
        You should receive a confirmation email within a few minutes — check your inbox.
      </Body>
      <PrimaryBtn onClick={() => navigate("/")}>Back to Home</PrimaryBtn>
      <GhostBtn onClick={() => navigate("/shop")}>Browse Machines</GhostBtn>
    </Card>
  );

  // ── Expired ───────────────────────────────────────────────────────────
  if (status === "expired") return (
    <Card>
      <IconCircle bg="#fef2f2">
        <AlertCircle style={{ height: 36, width: 36, color: "#dc2626" }} />
      </IconCircle>
      <Heading>Session Expired</Heading>
      <Body>
        Your checkout session timed out before payment was completed. No charge was made.
        Return to the shop and try again.
      </Body>
      <PrimaryBtn onClick={() => navigate("/shop")}>Browse Machines</PrimaryBtn>
      <GhostBtn onClick={() => navigate("/")}>Back to Home</GhostBtn>
    </Card>
  );

  // ── No session — generic fallback ────────────────────────────────────
  if (status === "no-session") return (
    <Card>
      <IconCircle bg="#f0fdf4">
        <CheckCircle2 style={{ height: 36, width: 36, color: "#16a34a" }} />
      </IconCircle>
      <Heading>Thank You!</Heading>
      <Body>
        If you just completed a purchase, your order has been received.
        A confirmation email with your receipt and next steps is on its way to your inbox.
      </Body>

      <div
        style={{
          background: "#f8f7ff",
          borderRadius: 12,
          padding: "14px 18px",
          marginBottom: 24,
          textAlign: "left",
          border: "1px solid rgba(106,0,244,0.1)",
          fontSize: 13,
          color: "#6b7280",
          fontFamily: "'Manrope', sans-serif",
          lineHeight: 1.6,
        }}
      >
        <span style={{ fontWeight: 700, color: "#374151" }}>Didn't get an email?</span>{" "}
        Check your spam folder, or reach out to{" "}
        <a href="mailto:support@vendbabee.com" style={{ color: "#6a00f4", textDecoration: "underline" }}>
          support@vendbabee.com
        </a>{" "}
        and we'll sort it out right away.
      </div>

      <PrimaryBtn onClick={() => navigate("/shop")}>
        Browse More Machines <ArrowRight size={16} />
      </PrimaryBtn>
      <GhostBtn onClick={() => navigate("/")}>Back to Home</GhostBtn>
    </Card>
  );

  // ── Error ─────────────────────────────────────────────────────────────
  return (
    <Card>
      <IconCircle bg="#fef2f2">
        <AlertCircle style={{ height: 36, width: 36, color: "#dc2626" }} />
      </IconCircle>
      <Heading>Verification Error</Heading>
      <Body>
        We couldn't verify your payment status. If you believe this is an error,
        contact us with your order reference and we'll look into it immediately.
      </Body>
      <PrimaryBtn onClick={() => navigate("/")}>Back to Home</PrimaryBtn>
      <GhostBtn onClick={() => navigate("/shop")}>Browse Machines</GhostBtn>
    </Card>
  );
}
